import { Capacitor, SystemBars, SystemBarsStyle } from '@capacitor/core';
import { router } from '@inertiajs/react';

const APP_ORIGIN = 'https://amortal.me';
const APP_SCHEME = 'me.amortal:';
const NATIVE_PLATFORMS = new Set(['ios', 'android']);
const OAUTH_PATHS = new Set(['/auth/google_oauth2', '/auth/apple']);
const APP_PATHS = ['/app', '/aplikacja'];

function browserEnvironment() {
  return typeof window === 'undefined' ? undefined : window;
}

export function resolveNativePlatform({
  environment = import.meta.env.MODE,
  search = browserEnvironment()?.location.search ?? '',
  native = Capacitor.isNativePlatform(),
  platform = Capacitor.getPlatform(),
} = {}) {
  const previewPlatform = new URLSearchParams(search).get('platform');

  if (environment !== 'production' && NATIVE_PLATFORMS.has(previewPlatform)) {
    return { platform: previewPlatform, native: true, preview: true };
  }

  if (native && NATIVE_PLATFORMS.has(platform)) {
    return { platform, native: true, preview: false };
  }

  return { platform: 'web', native: false, preview: false };
}

function normalizedNativeUrl(value) {
  try {
    const url = new URL(value);

    if (url.protocol === APP_SCHEME) {
      const path = `/${url.host}${url.pathname}`.replace(/\/{2,}/g, '/');
      return { path, search: url.search };
    }

    if (url.protocol === 'https:' && url.origin === APP_ORIGIN) {
      return { path: url.pathname, search: url.search };
    }
  } catch {
    return null;
  }

  return null;
}

export function nativePathFromUrl(value) {
  const parsed = normalizedNativeUrl(value);
  if (!parsed) return null;

  const allowed = APP_PATHS.some(
    (path) => parsed.path === path || parsed.path.startsWith(`${path}/`),
  );
  const oauthCallback = parsed.path === '/auth/capacitor-callback';

  return allowed || oauthCallback ? `${parsed.path}${parsed.search}` : null;
}

export function handleNativeAppUrl({
  url,
  entryPath = '/app',
  router: inertiaRouter = router,
  history = browserEnvironment()?.history,
  location = browserEnvironment()?.location,
}) {
  const path = nativePathFromUrl(url);
  if (!path) return false;

  if (path.startsWith('/auth/capacitor-callback')) {
    location?.assign(path);
    return true;
  }

  if (history && browserEnvironment()?.location.pathname === '/') {
    history.replaceState(null, '', entryPath);
  } else if (history && !browserEnvironment()) {
    history.replaceState(null, '', entryPath);
  }
  inertiaRouter.visit(path);
  return true;
}

export function createBackButtonHandler({
  keyboard,
  app,
  history = browserEnvironment()?.history,
  keyboardVisible = () =>
    browserEnvironment()?.document.documentElement.classList.contains(
      'keyboard-visible',
    ),
}) {
  return ({ canGoBack }) => {
    if (keyboardVisible()) {
      void keyboard.hide();
    } else if (canGoBack) {
      history?.back();
    } else {
      void app.exitApp();
    }
  };
}

export function createForegroundHandlers(inertiaRouter = router) {
  let paused = false;

  return {
    pause: () => {
      paused = true;
    },
    resume: () => {
      if (!paused) return;

      paused = false;
      inertiaRouter.reload({ preserveScroll: true, preserveState: true });
    },
  };
}

function addPlatformClasses(platform, document) {
  for (const element of [document.documentElement, document.body]) {
    element.classList.add('native', `native-${platform}`);
  }
}

function observeVisualViewport(window, document) {
  const viewport = window.visualViewport;
  if (!viewport) return;

  const update = () => {
    document.documentElement.style.setProperty(
      '--visual-viewport-height',
      `${viewport.height}px`,
    );
    document.documentElement.style.setProperty(
      '--visual-viewport-offset-top',
      `${viewport.offsetTop}px`,
    );
  };

  update();
  viewport.addEventListener('resize', update);
  viewport.addEventListener('scroll', update);
}

export function systemBarAppearance({ background, dark }) {
  return {
    color: background,
    darkButtons: !dark,
    style: dark ? SystemBarsStyle.Dark : SystemBarsStyle.Light,
  };
}

export async function applySystemBarAppearance({
  appearance,
  platform,
  NavigationBar,
  StatusBar,
  SystemBars,
}) {
  const updates = [
    SystemBars.setStyle({ style: appearance.style }),
    StatusBar.setStyle({ style: appearance.style }),
  ];

  if (platform === 'android') {
    updates.push(StatusBar.setBackgroundColor({ color: appearance.color }));
    if (NavigationBar) {
      updates.push(
        NavigationBar.setNavigationBarColor({
          color: appearance.color,
          darkButtons: appearance.darkButtons,
          dividerColor: appearance.color,
        }),
      );
    }
  }

  await Promise.all(updates);
}

async function synchronizeSystemBars(document, platform) {
  const [{ StatusBar }, navigationBarModule] = await Promise.all([
    import('@capacitor/status-bar'),
    platform === 'android'
      ? import('@capgo/capacitor-navigation-bar')
      : Promise.resolve({ NavigationBar: null }),
  ]);
  const NavigationBar = navigationBarModule.NavigationBar;
  const synchronize = async () => {
    const appearance = systemBarAppearance({
      background: document.defaultView
        .getComputedStyle(document.documentElement)
        .getPropertyValue('--background')
        .trim(),
      dark: document.documentElement.classList.contains('dark'),
    });
    await applySystemBarAppearance({
      appearance,
      platform,
      NavigationBar,
      StatusBar,
      SystemBars,
    });
  };

  await synchronize();
  const observer = new MutationObserver(() => void synchronize());
  observer.observe(document.documentElement, {
    attributeFilter: ['class'],
    attributes: true,
  });
}

function hideSplashAfterHydration(SplashScreen, document, window) {
  let hidden = false;
  let timeout;
  let observer;
  const hide = () => {
    if (hidden) return;

    hidden = true;
    observer?.disconnect();
    window.clearTimeout(timeout);
    window.requestAnimationFrame(() => void SplashScreen.hide());
  };
  const root = document.getElementById('app');

  if (root?.childElementCount) {
    hide();
    return;
  }

  observer = new MutationObserver(() => {
    if (root?.childElementCount) hide();
  });
  if (root) observer.observe(root, { childList: true });
  timeout = window.setTimeout(hide, 5_000);
}

async function initializeNativePlatform({ platform, entryPath }) {
  const window = browserEnvironment();
  const document = window.document;
  const [{ App }, { Keyboard, KeyboardResize }, { SplashScreen }] =
    await Promise.all([
      import('@capacitor/app'),
      import('@capacitor/keyboard'),
      import('@capacitor/splash-screen'),
    ]);

  addPlatformClasses(platform, document);
  observeVisualViewport(window, document);
  await synchronizeSystemBars(document, platform);
  hideSplashAfterHydration(SplashScreen, document, window);

  if (platform === 'ios') {
    await Promise.all([
      Keyboard.setAccessoryBarVisible({ isVisible: false }),
      Keyboard.setScroll({ isDisabled: true }),
      Keyboard.setResizeMode({ mode: KeyboardResize.Native }),
    ]);
  }

  let keyboardVisible = false;
  const setKeyboardState = (visible, height = 0) => {
    keyboardVisible = visible;
    for (const element of [document.documentElement, document.body]) {
      element.classList.toggle('keyboard-visible', visible);
    }
    document.documentElement.style.setProperty(
      '--keyboard-height',
      `${height}px`,
    );
  };

  await Keyboard.addListener('keyboardWillShow', ({ keyboardHeight }) => {
    setKeyboardState(true, keyboardHeight);
  });
  await Keyboard.addListener('keyboardWillHide', () => setKeyboardState(false));

  const { installTapOutsideDismissal } =
    await import('@/lib/tap-outside-dismiss');
  installTapOutsideDismissal({ document, keyboard: Keyboard });

  const foreground = createForegroundHandlers();
  await App.addListener('pause', foreground.pause);
  await App.addListener('resume', foreground.resume);
  await App.addListener(
    'backButton',
    createBackButtonHandler({
      app: App,
      keyboard: Keyboard,
      keyboardVisible: () => keyboardVisible,
    }),
  );
  await App.addListener('appUrlOpen', ({ url }) => {
    handleNativeAppUrl({ url, entryPath });
  });

  const launch = await App.getLaunchUrl();
  if (launch?.url) handleNativeAppUrl({ url: launch.url, entryPath });

  return { platform, native: true, preview: false };
}

export function createNativeRuntime({
  resolvePlatform = resolveNativePlatform,
  initializeNative = initializeNativePlatform,
} = {}) {
  let initialization;
  let detected;

  return {
    get platform() {
      detected ||= resolvePlatform();
      return detected;
    },
    init({ entryPath = '/app' } = {}) {
      detected ||= resolvePlatform();
      if (!detected.native) return Promise.resolve(detected);

      if (!initialization) {
        if (detected.preview) {
          addPlatformClasses(detected.platform, browserEnvironment().document);
          initialization = Promise.resolve(detected);
        } else {
          initialization = initializeNative({
            platform: detected.platform,
            entryPath,
          });
        }
      }

      return initialization;
    },
  };
}

const nativeRuntime = createNativeRuntime();

export function isNative() {
  return nativeRuntime.platform.native;
}

export function hasNativeBridge(runtime = nativeRuntime.platform) {
  return runtime.native && !runtime.preview;
}

export function isIOS() {
  return nativeRuntime.platform.platform === 'ios';
}

export function isAndroid() {
  return nativeRuntime.platform.platform === 'android';
}

export function initCapacitor(options) {
  return nativeRuntime.init(options);
}

export function nativeOauthRequestUrl(
  path,
  origin = browserEnvironment()?.location.origin,
) {
  if (!OAUTH_PATHS.has(path)) return null;

  const url = new URL(path, origin);
  url.searchParams.set('capacitor', '1');
  return url.toString();
}

async function loadOauthPlugins() {
  const [{ App: app }, { Browser: browser }] = await Promise.all([
    import('@capacitor/app'),
    import('@capacitor/browser'),
  ]);

  return { app, browser };
}

async function startOauthSession({ browser, app, onCleanup, url }) {
  let appUrlListener;
  let browserFinishedListener;
  let cleanupPromise;
  const cleanup = () => {
    cleanupPromise ||= Promise.allSettled([
      appUrlListener?.remove(),
      browserFinishedListener?.remove(),
    ]).then(onCleanup);
    return cleanupPromise;
  };

  try {
    appUrlListener = await app.addListener(
      'appUrlOpen',
      async ({ url: callbackUrl }) => {
        if (
          nativePathFromUrl(callbackUrl)?.split('?')[0] !==
          '/auth/capacitor-callback'
        ) {
          return;
        }

        try {
          await browser.close();
        } finally {
          await cleanup();
        }
      },
    );
    browserFinishedListener = await browser.addListener(
      'browserFinished',
      cleanup,
    );
    await browser.open({ presentationStyle: 'popover', url });
    return true;
  } catch (error) {
    await cleanup();
    throw error;
  }
}

export function createNativeOauthLauncher({
  isNativePlatform = () => Capacitor.isNativePlatform(),
  loadPlugins = loadOauthPlugins,
  origin = () => browserEnvironment()?.location.origin,
} = {}) {
  let activeSession;

  return function launch(path) {
    const url = nativeOauthRequestUrl(path, origin());
    if (!url) return Promise.resolve(false);

    if (!isNativePlatform()) {
      browserEnvironment()?.location.assign(url);
      return Promise.resolve(true);
    }
    if (activeSession) return activeSession.result;

    const session = {};
    activeSession = session;
    session.result = loadPlugins()
      .then(({ app, browser }) =>
        startOauthSession({
          app,
          browser,
          onCleanup: () => {
            if (activeSession === session) activeSession = null;
          },
          url,
        }),
      )
      .catch((error) => {
        if (activeSession === session) activeSession = null;
        throw error;
      });
    return session.result;
  };
}

const launchNativeOauth = createNativeOauthLauncher();

export function openNativeOAuth(path) {
  return launchNativeOauth(path);
}
